/**
 * Replaceable parts (bulbs, filters, batteries, remotes, other) and the owner's saved custom part types.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { spareName, type PropertyPart } from "@/lib/parts";

const key = (propertyId: string) => ["property-parts", propertyId] as const;

// The generated Supabase types predate these tables, so go through an untyped handle.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = () => supabase as unknown as { from: (t: string) => any };

export function usePropertyParts(propertyId: string | undefined) {
  return useQuery({
    queryKey: key(propertyId ?? ""),
    queryFn: async (): Promise<PropertyPart[]> => {
      if (!propertyId) return [];
      const { data, error } = await db()
        .from("property_parts")
        .select("*")
        .eq("property_id", propertyId)
        .order("room", { ascending: true })
        .order("created_at", { ascending: true });
      // Before the migration is run the table does not exist: behave as "no parts yet".
      if (error) {
        if (/relation .* does not exist|schema cache/i.test(error.message ?? "")) return [];
        throw error;
      }
      return (data ?? []) as PropertyPart[];
    },
    enabled: !!propertyId,
  });
}

export type PartInput = Partial<Omit<PropertyPart, "id" | "property_id">> & { name: string };

/** Keep the auto spare supply line in step with a part: on when "cleaner can replace", off (kept) otherwise. */
async function syncSpare(part: PropertyPart): Promise<void> {
  const client = db();
  if (part.cleaner_can_replace) {
    const name = spareName(part);
    if (part.spare_inventory_item_id) {
      await client
        .from("inventory_items")
        .update({ item_name: name, threshold_count: Math.max(0, part.keep_spares) })
        .eq("id", part.spare_inventory_item_id);
      return;
    }
    const { data, error } = await client
      .from("inventory_items")
      .insert({
        property_id: part.property_id,
        item_name: name,
        threshold_count: Math.max(0, part.keep_spares),
        current_count: 0,
        in_stock_qty: 0,
        ordered_qty: 0,
        quantity_type: "units",
        inventory_scope: "supply_closet",
        photo_url: part.photo_label_url || part.photo_place_url || null,
        spare_for_part_id: part.id,
      })
      .select("id")
      .single();
    if (error) throw error;
    await client.from("property_parts").update({ spare_inventory_item_id: (data as { id: string }).id }).eq("id", part.id);
  }
}

export function useCreatePart(propertyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: PartInput) => {
      const { data, error } = await db()
        .from("property_parts")
        .insert({ ...input, property_id: propertyId })
        .select("*")
        .single();
      if (error) throw error;
      const part = data as PropertyPart;
      await syncSpare(part);
      return part;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: key(propertyId) });
      void qc.invalidateQueries({ queryKey: ["inventory", propertyId] });
      void qc.invalidateQueries({ queryKey: ["inventory-cleaner", propertyId] });
    },
  });
}

export function useUpdatePart(propertyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...patch }: Partial<PropertyPart> & { id: string }) => {
      const { data, error } = await db().from("property_parts").update(patch).eq("id", id).select("*").single();
      if (error) throw error;
      const part = data as PropertyPart;
      await syncSpare(part);
      return part;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: key(propertyId) });
      void qc.invalidateQueries({ queryKey: ["inventory", propertyId] });
      void qc.invalidateQueries({ queryKey: ["inventory-cleaner", propertyId] });
    },
  });
}

export function useDeletePart(propertyId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (part: PropertyPart) => {
      const client = db();
      // The spare line is only a copy of the part; it goes with it.
      if (part.spare_inventory_item_id) {
        await client.from("inventory_items").delete().eq("id", part.spare_inventory_item_id);
      }
      const { error } = await client.from("property_parts").delete().eq("id", part.id);
      if (error) throw error;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: key(propertyId) });
      void qc.invalidateQueries({ queryKey: ["inventory", propertyId] });
      void qc.invalidateQueries({ queryKey: ["inventory-cleaner", propertyId] });
    },
  });
}

export interface CustomPartType {
  id: string;
  name: string;
  spec_labels: string[];
}

export function useCustomPartTypes() {
  return useQuery({
    queryKey: ["part-types"],
    queryFn: async (): Promise<CustomPartType[]> => {
      const { data, error } = await db().from("part_types").select("id, name, spec_labels").order("name");
      if (error) {
        if (/relation .* does not exist|schema cache/i.test(error.message ?? "")) return [];
        throw error;
      }
      return (data ?? []) as CustomPartType[];
    },
  });
}

export function useCreateCustomPartType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { name: string; spec_labels: string[] }) => {
      const { data: auth } = await supabase.auth.getUser();
      const uid = auth.user?.id;
      if (!uid) throw new Error("Sign in again to save a part type");
      const { data, error } = await db()
        .from("part_types")
        .insert({ owner_id: uid, name: input.name, spec_labels: input.spec_labels })
        .select("id, name, spec_labels")
        .single();
      if (error) throw error;
      return data as CustomPartType;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["part-types"] }),
  });
}
